'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import {
  Mail, ArrowRight, CheckCircle2, Loader2, Users, Calendar,
  QrCode, Wifi, Shield, RefreshCw, AlertCircle, KeyRound,
  Sun, Moon, Monitor, Accessibility, XCircle, Lock, Eye, EyeOff,
  UserCog, LogIn,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';
import { useTheme } from '@/contexts/ThemeContext';
import { useAccessibility } from '@/contexts/AccessibilityContext';

// ─── Types ────────────────────────────────────────────────────────────────────

type AuthMode = 'delegate' | 'admin';
type DelegateStep =
  | 'email' |'denied' |'qr-checkin' |'set-pin' |'enter-pin' |'forgot-email' |'forgot-otp' |'forgot-reset';

interface Delegate {
  id: string;
  name: string;
  organization: string;
  role: string;
  email: string;
  cell: string;
  pin_set: boolean;
  checked_in: boolean;
  pin_reset_required?: boolean;
}

interface QRToken {
  id: string;
  token: string;
  used: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateOTP(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

async function sendDelegateEmail(payload: {
  type: string;
  to: string;
  name: string;
  otp?: string;
  organization?: string;
}) {
  try {
    await fetch(`${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/send-delegate-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    // non-blocking
  }
}

async function setDelegateSessionCookie(delegateId: string) {
  await fetch('/api/auth/delegate-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ delegateId }),
  });
  if (typeof document !== 'undefined') {
    const isSecure = location.protocol === 'https:';
    const secure = isSecure ? '; Secure' : '';
    document.cookie = `delegate_session=true; path=/; max-age=86400; SameSite=Lax${secure}`;
    document.cookie = `delegate_id=${delegateId}; path=/; max-age=86400; SameSite=Lax${secure}`;
  }
  await new Promise(r => setTimeout(r, 150));
}

async function setAdminSessionCookie() {
  await fetch('/api/auth/admin-session', { method: 'POST' });
  if (typeof document !== 'undefined') {
    const isSecure = location.protocol === 'https:';
    document.cookie = `admin_session=true; path=/; max-age=86400; SameSite=Lax${isSecure ? '; Secure' : ''}`;
  }
  await new Promise(r => setTimeout(r, 150));
}

// ─── PIN / OTP input renderers ────────────────────────────────────────────────

function PinInputs({
  digits,
  setDigits,
  refs,
}: {
  digits: string[];
  setDigits: React.Dispatch<React.SetStateAction<string[]>>;
  refs: React.RefObject<HTMLInputElement>[];
}) {
  return (
    <div className="flex gap-3 justify-center">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={e => {
            const v = e.target.value.replace(/\D/g, '').slice(-1);
            const next = [...digits];
            next[i] = v;
            setDigits(next);
            if (v && i < refs.length - 1) refs[i + 1]?.current?.focus();
          }}
          onKeyDown={e => {
            if (e.key === 'Backspace' && !d && i > 0) refs[i - 1]?.current?.focus();
          }}
          className="w-14 h-14 text-center text-2xl font-bold rounded-xl border-2 border-[hsl(var(--border))] bg-white focus:outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] transition-all"
        />
      ))}
    </div>
  );
}

function OtpInputs({
  digits,
  setDigits,
  refs,
}: {
  digits: string[];
  setDigits: React.Dispatch<React.SetStateAction<string[]>>;
  refs: React.RefObject<HTMLInputElement>[];
}) {
  return (
    <div className="flex gap-2 justify-center">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={e => {
            const v = e.target.value.replace(/\D/g, '').slice(-1);
            const next = [...digits];
            next[i] = v;
            setDigits(next);
            if (v && i < refs.length - 1) refs[i + 1]?.current?.focus();
          }}
          onKeyDown={e => {
            if (e.key === 'Backspace' && !d && i > 0) refs[i - 1]?.current?.focus();
          }}
          onPaste={e => {
            e.preventDefault();
            const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, refs.length);
            const next = [...digits];
            pasted.split('').forEach((ch, idx) => {
              if (idx < refs.length) next[idx] = ch;
            });
            setDigits(next);
            refs[Math.min(pasted.length, refs.length - 1)]?.current?.focus();
          }}
          className="w-11 h-12 text-center text-xl font-bold rounded-xl border-2 border-[hsl(var(--border))] bg-white focus:outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] transition-all"
        />
      ))}
    </div>
  );
}

// ─── Admin Login Panel ────────────────────────────────────────────────────────

function AdminLoginPanel({ onBack }: { onBack: () => void }) {
  const supabase = createClient();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password.');
      return;
    }
    setLoading(true);
    try {
      // Look up admin credentials from DB
      const { data: admin, error: dbError } = await supabase
        .from('admin_credentials')
        .select('id, username, password_hash, display_name, is_active')
        .eq('username', username.trim().toLowerCase())
        .eq('is_active', true)
        .maybeSingle();

      if (dbError) {
        setError('Unable to reach the database. Please try again.');
        return;
      }

      if (!admin) {
        setError('Invalid username or password.');
        return;
      }

      // Verify password using same btoa encoding as delegate PINs
      const expectedHash = btoa(`${username.trim().toLowerCase()}:${password}`);
      if (admin.password_hash !== expectedHash) {
        setError('Invalid username or password.');
        return;
      }

      // Update last login
      await supabase
        .from('admin_credentials')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', admin.id);

      await setAdminSessionCookie();
      localStorage.setItem('admin_session', 'true');
      localStorage.setItem('admin_username', admin.username);
      toast.success(`Welcome back, ${admin.display_name}!`);
      window.location.replace('/registration-admin');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors"
        >
          ← Back
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))] flex items-center justify-center shrink-0">
          <Shield size={20} className="text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-[hsl(var(--foreground))]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Admin Portal
          </h2>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Restricted access — authorised personnel only</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-1.5">
            Username
          </label>
          <input
            type="text"
            value={username}
            onChange={e => { setUsername(e.target.value); setError(''); }}
            placeholder="Admin username"
            autoComplete="username"
            autoFocus
            className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] text-sm bg-[hsl(var(--background))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary))] transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              placeholder="Admin password"
              autoComplete="current-password"
              className="w-full px-3 py-2.5 pr-10 rounded-lg border border-[hsl(var(--border))] text-sm bg-[hsl(var(--background))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary))] transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPw(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            >
              {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <Lock size={12} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-[hsl(var(--primary))] text-white rounded-lg font-semibold text-sm hover:opacity-90 active:scale-95 transition-all disabled:opacity-60"
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <><Lock size={14} /><span>Sign In to Admin</span></>
          )}
        </button>
      </form>

      <div className="mt-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-3">
        <p className="text-[10px] text-[hsl(var(--muted-foreground))] text-center">
          Admin credentials are managed by the SEDFA system administrator.
        </p>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LoginClient() {
  const supabase = createClient();
  const { theme, setTheme } = useTheme();
  const { highContrast, setHighContrast, focusIndicators, setFocusIndicators, screenReaderMode, setScreenReaderMode, dyslexicFont, setDyslexicFont } = useAccessibility();

  // ── Auth mode ──
  const [authMode, setAuthMode] = useState<AuthMode>('delegate');

  // ── UI state ──
  const [wcagOpen, setWcagOpen] = useState(false);
  const [step, setStep] = useState<DelegateStep>('email');
  const [loading, setLoading] = useState(false);

  // ── Delegate count ──
  const [delegateCount, setDelegateCount] = useState<number | null>(null);

  // ── Event settings ──
  const [eventName, setEventName] = useState('MSME & Cooperatives Inclusive Growth Forum');
  const [eventDate, setEventDate] = useState('9 April 2026');
  const [mainVenue, setMainVenue] = useState('East London Golf Club');

  // ── Email step ──
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // ── Delegate data ──
  const [delegate, setDelegate] = useState<Delegate | null>(null);
  const [qrToken, setQrToken] = useState<QRToken | null>(null);
  const [qrScanned, setQrScanned] = useState(false);
  const [polling, setPolling] = useState(false);

  // ── PIN state ──
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState('');
  const pinRefs = [
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
  ];
  const confirmPinRefs = [
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
  ];

  // ── OTP state ──
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [otpResending, setOtpResending] = useState(false);
  const otpRefs = [
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
  ];

  // ── Forgot PIN state ──
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailError, setForgotEmailError] = useState('');
  const [forgotDelegate, setForgotDelegate] = useState<Delegate | null>(null);
  const [forgotOtp, setForgotOtp] = useState(['', '', '', '', '', '']);
  const [forgotOtpError, setForgotOtpError] = useState('');
  const [forgotPin, setForgotPin] = useState(['', '', '', '']);
  const [forgotConfirmPin, setForgotConfirmPin] = useState(['', '', '', '']);
  const [forgotPinError, setForgotPinError] = useState('');
  const forgotOtpRefs = [
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
  ];
  const forgotPinRefs = [
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
  ];
  const forgotConfirmPinRefs = [
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
  ];

  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // Check for ?mode=admin in URL on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mode') === 'admin') setAuthMode('admin');
    }
  }, []);

  // Fetch delegate count and event settings on mount
  useEffect(() => {
    const fetchData = async () => {
      const [countResult, settingsResult] = await Promise.all([
        supabase.from('delegates').select('id', { count: 'exact', head: true }),
        supabase.from('event_settings').select('event_name, event_date, main_venue').limit(1).maybeSingle(),
      ]);
      if (countResult.count !== null) setDelegateCount(countResult.count);
      if (settingsResult.data) {
        setEventName(settingsResult.data.event_name);
        setEventDate(settingsResult.data.event_date);
        // Extract short venue name (before first comma)
        setMainVenue(settingsResult.data.main_venue.split(',')[0]);
      }
    };
    fetchData();
  }, [supabase]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // Poll for QR scan
  useEffect(() => {
    if (step !== 'qr-checkin' || !qrToken || qrScanned) return;
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await supabase
          .from('delegate_qr_tokens')
          .select('used')
          .eq('id', qrToken.id)
          .single();
        if (data?.used) {
          clearInterval(pollRef.current!);
          setPolling(false);
          setQrScanned(true);
          toast.success('QR code scanned! Please enter your PIN.');
          setTimeout(() => {
            setPin(['', '', '', '']);
            setPinError('');
            setStep('enter-pin');
          }, 1200);
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [step, qrToken, qrScanned]);

  // ── OTP countdown ──────────────────────────────────────────────────────────

  const startCountdown = () => {
    setOtpCountdown(60);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setOtpCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // ── OTP sender ─────────────────────────────────────────────────────────────

  const sendOtp = async (del: Delegate, purpose: 'pin_verify' | 'forgot_pin') => {
    const code = generateOTP();
    const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString();
    await supabase
      .from('delegate_otp_codes')
      .update({ used: true })
      .eq('delegate_id', del.id)
      .eq('purpose', purpose)
      .eq('used', false);
    const { error } = await supabase.from('delegate_otp_codes').insert({
      delegate_id: del.id,
      email: del.email,
      code,
      purpose,
      expires_at: expiresAt,
    });
    if (error) throw new Error('Failed to generate OTP');
    await sendDelegateEmail({ type: 'otp', to: del.email, name: del.name.split(' ')[0], otp: code });
  };

  // ── Reset to start ─────────────────────────────────────────────────────────

  const resetToEmail = () => {
    setStep('email');
    setEmail('');
    setEmailError('');
    setDelegate(null);
    setQrToken(null);
    setQrScanned(false);
    setPin(['', '', '', '']);
    setConfirmPin(['', '', '', '']);
    setPinError('');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
    setOtpCountdown(0);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // DELEGATE STEP HANDLERS
  // ══════════════════════════════════════════════════════════════════════════

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    const trimmed = email.toLowerCase().trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const { data: del, error } = await supabase
        .from('delegates')
        .select('id, name, organization, role, email, cell, pin_set, checked_in, pin_reset_required')
        .eq('email', trimmed)
        .maybeSingle();

      if (error) {
        setEmailError('Unable to reach the database. Please check your connection and try again.');
        return;
      }

      if (!del) {
        setStep('denied');
        return;
      }

      setDelegate(del);

      // Always show QR code first — fetch or create a QR token
      const { data: existing, error: qrFetchError } = await supabase
        .from('delegate_qr_tokens')
        .select('id, token, used')
        .eq('delegate_id', del.id)
        .eq('used', false)
        .maybeSingle();

      if (qrFetchError) {
        setEmailError('Failed to load QR token. Please try again.');
        return;
      }

      if (existing) {
        setQrToken(existing);
      } else {
        const token = `SEDFA2026-${del.id.slice(0, 8).toUpperCase()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
        const { data: newToken, error: tokenError } = await supabase
          .from('delegate_qr_tokens')
          .insert({ delegate_id: del.id, token })
          .select('id, token, used')
          .single();
        if (tokenError || !newToken) {
          setEmailError('Failed to generate QR code. Please try again.');
          return;
        }
        setQrToken(newToken);
      }

      setQrScanned(false);
      setStep('qr-checkin');
    } catch {
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnterPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinError('');
    const pinStr = pin.join('');
    if (pinStr.length < 4) { setPinError('Please enter your 4-digit PIN.'); return; }
    if (!delegate) return;
    setLoading(true);
    let navigating = false;
    try {
      const { data: row, error: pinFetchError } = await supabase
        .from('delegates')
        .select('pin_hash, pin_reset_required')
        .eq('id', delegate.id)
        .single();

      if (pinFetchError || !row) {
        setPinError('Unable to verify PIN. Please try again.');
        return;
      }

      const expected = btoa(`${delegate.id}:${pinStr}`);
      if (row.pin_hash !== expected) {
        setPinError('Incorrect PIN. Please try again.');
        setPin(['', '', '', '']);
        pinRefs[0]?.current?.focus();
        return;
      }

      // If delegate entered default PIN (1234) or pin_reset_required, prompt to set personal PIN
      if (pinStr === '1234' || row.pin_reset_required) {
        setPin(['', '', '', '']);
        setConfirmPin(['', '', '', '']);
        setPinError('');
        toast.info('Please set your own personal PIN to continue.');
        setStep('set-pin');
        setLoading(false);
        return;
      }

      navigating = true;
      await setDelegateSessionCookie(delegate.id);
      localStorage.setItem('delegate_session', 'true');
      localStorage.setItem('delegate_id', delegate.id);
      toast.success(`Welcome, ${delegate.name.split(' ')[0]}! You're all set.`);
      window.location.replace('/delegate-dashboard');
    } catch {
      setPinError('Something went wrong. Please try again.');
    } finally {
      if (!navigating) setLoading(false);
    }
  };

  const handleSetPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinError('');
    const pinStr = pin.join('');
    const confirmStr = confirmPin.join('');
    if (pinStr.length < 4) { setPinError('Please enter all 4 digits.'); return; }
    if (pinStr !== confirmStr) { setPinError('PINs do not match. Please try again.'); return; }
    if (!delegate) return;
    setLoading(true);
    let navigating = false;
    try {
      const pinEncoded = btoa(`${delegate.id}:${pinStr}`);
      const { error } = await supabase
        .from('delegates')
        .update({ pin_hash: pinEncoded, pin_set: true, pin_reset_required: false })
        .eq('id', delegate.id);

      if (error) { setPinError('Failed to save PIN. Please try again.'); return; }

      navigating = true;
      await setDelegateSessionCookie(delegate.id);
      localStorage.setItem('delegate_session', 'true');
      localStorage.setItem('delegate_id', delegate.id);
      toast.success(`Welcome, ${delegate.name.split(' ')[0]}! You're all set.`);
      window.location.replace('/delegate-dashboard');
    } catch {
      setPinError('Something went wrong. Please try again.');
    } finally {
      if (!navigating) setLoading(false);
    }
  };

  const qrValue =
    qrToken && delegate
      ? JSON.stringify({
          id: delegate.id,
          name: delegate.name,
          org: delegate.organization,
          token: qrToken.token,
          event: 'SEDFA2026',
        })
      : '';

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen flex overflow-x-hidden">
      <Toaster position="bottom-right" richColors />

      {/* ── Brand Panel ── */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-[hsl(var(--primary))] text-white p-10 relative overflow-hidden shrink-0">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="relative z-10">
          <div className="flex flex-col items-start gap-3 mb-12">
            <AppLogo size={80} />
            <span className="text-xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              SEDFA Forum Hub
            </span>
          </div>
          <h1
            className="text-4xl font-bold leading-tight mb-4"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            {eventName}
          </h1>
          <p className="text-amber-100 text-lg mb-2">{eventDate} · {mainVenue}</p>
          <p className="text-amber-200 text-sm">East London Golf Club</p>
        </div>
        <div className="relative z-10 space-y-4">
          {[
            {
              icon: Users,
              label: delegateCount !== null ? `${delegateCount} delegates pre-registered` : 'Loading…',
              sub: 'Pre-registered delegates',
            },
            { icon: Calendar, label: '18 Sessions', sub: 'Across 4 tracks' },
            { icon: QrCode, label: 'QR Check-in', sub: 'Fast-track registration' },
            { icon: Wifi, label: 'Live Q&A', sub: 'Real-time engagement' },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <item.icon size={18} className="text-amber-200" />
              </div>
              <div>
                <p className="text-sm font-semibold">{item.label}</p>
                <p className="text-xs text-amber-200">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="relative z-10 text-xs text-amber-300 mt-6">© 2026 SEDFA. All rights reserved.</p>
      </div>

      {/* ── Form Panel ── */}
      <div className="flex-1 min-w-0 flex flex-col justify-center items-center px-6 py-10 bg-[hsl(var(--background))] overflow-x-hidden">
        <div className="w-full max-w-md">

          {/* Mobile Logo */}
          <div className="flex flex-col items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={72} />
            <span className="font-bold text-[hsl(var(--primary))]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              SEDFA Forum Hub
            </span>
          </div>

          {/* Theme & WCAG Toggles */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="flex items-center gap-1 bg-[hsl(var(--muted))] rounded-lg p-1">
              {([['light', Sun, 'Light'], ['system', Monitor, 'Auto'], ['dark', Moon, 'Dark']] as const).map(
                ([val, IconComp, label]) => (
                  <button
                    key={val}
                    onClick={() => setTheme(val)}
                    aria-label={`${label} mode`}
                    aria-pressed={theme === val}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                      theme === val
                        ? 'bg-white text-[hsl(var(--primary))]'
                        : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
                    }`}
                  >
                    <IconComp size={13} />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                )
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setWcagOpen(o => !o)}
                aria-label="WCAG 2.1 accessibility options"
                aria-expanded={wcagOpen}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                  wcagOpen
                    ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]'
                    : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-transparent hover:text-[hsl(var(--foreground))]'
                }`}
              >
                <Accessibility size={13} />
                <span className="hidden sm:inline">WCAG 2.1</span>
              </button>
              {wcagOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-[hsl(var(--border))] rounded-xl shadow-xl z-50 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-bold text-[hsl(var(--foreground))] uppercase tracking-wide">
                      Accessibility (WCAG 2.1)
                    </h3>
                    <button
                      onClick={() => setWcagOpen(false)}
                      className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] text-lg leading-none"
                    >
                      ×
                    </button>
                  </div>
                  <div className="space-y-3">
                    {[
                      {
                        label: 'High Contrast',
                        sub: 'WCAG 1.4.3',
                        checked: highContrast === 'high',
                        toggle: () => setHighContrast(highContrast === 'high' ? 'normal' : 'high'),
                      },
                      {
                        label: 'Focus Indicators',
                        sub: 'WCAG 2.4.7',
                        checked: focusIndicators,
                        toggle: () => setFocusIndicators(!focusIndicators),
                      },
                      {
                        label: 'Screen Reader Mode',
                        sub: 'WCAG 4.1.2',
                        checked: screenReaderMode,
                        toggle: () => setScreenReaderMode(!screenReaderMode),
                      },
                      {
                        label: 'Dyslexia-Friendly Font',
                        sub: 'WCAG 1.4.8',
                        checked: dyslexicFont,
                        toggle: () => setDyslexicFont(!dyslexicFont),
                      },
                    ].map(item => (
                      <div key={item.label} className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-[hsl(var(--foreground))]">{item.label}</p>
                          <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{item.sub}</p>
                        </div>
                        <button
                          role="switch"
                          aria-checked={item.checked}
                          onClick={item.toggle}
                          className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.4)] ${
                            item.checked ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                              item.checked ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Auth Mode Tabs ── */}
          {authMode === 'delegate' && step === 'email' && (
            <div className="flex rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-1 mb-6">
              <button
                onClick={() => setAuthMode('delegate')}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold bg-white text-[hsl(var(--primary))] shadow-sm transition-all"
              >
                <Users size={15} />
                Delegate
              </button>
              <button
                onClick={() => setAuthMode('admin')}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-all"
              >
                <UserCog size={15} />
                Admin
              </button>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              ADMIN MODE
          ════════════════════════════════════════════════════════════════ */}
          {authMode === 'admin' && (
            <AdminLoginPanel onBack={() => setAuthMode('delegate')} />
          )}

          {/* ════════════════════════════════════════════════════════════════
              DELEGATE MODE
          ════════════════════════════════════════════════════════════════ */}
          {authMode === 'delegate' && (
            <>
              {/* STEP: EMAIL */}
              {step === 'email' && (
                <div>
                  <h2
                    className="text-2xl font-bold text-[hsl(var(--foreground))] mb-1"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    Delegate Access
                  </h2>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8">
                    Enter your registered email to continue.
                  </p>
                  <form onSubmit={handleEmailSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-1.5">
                        Registration Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                        <input
                          type="email"
                          value={email}
                          onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                          placeholder="yourname@organisation.co.za"
                          autoFocus
                          className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-[hsl(var(--border))] text-sm text-black focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.4)] focus:border-[hsl(var(--primary))] transition-all"
                        />
                      </div>
                      {emailError && (
                        <div className="flex items-start gap-1.5 mt-1.5">
                          <AlertCircle size={13} className="text-red-500 mt-0.5 shrink-0" />
                          <p className="text-xs text-red-500">{emailError}</p>
                        </div>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-[hsl(var(--primary))] text-white rounded-lg font-semibold text-sm hover:opacity-90 active:scale-95 transition-all disabled:opacity-60"
                    >
                      {loading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <><span>Continue</span><ArrowRight size={16} /></>
                      )}
                    </button>
                  </form>
                  <div className="mt-5 text-center">
                    <button
                      onClick={() => { setStep('forgot-email'); setForgotEmail(''); setForgotEmailError(''); }}
                      className="text-xs text-[hsl(var(--primary))] hover:underline font-medium"
                    >
                      Forgot PIN?
                    </button>
                  </div>
                </div>
              )}

              {/* STEP: DENIED */}
              {step === 'denied' && (
                <div className="text-center">
                  <div className="flex justify-center mb-5">
                    <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                      <XCircle size={44} className="text-red-500" />
                    </div>
                  </div>
                  <h2
                    className="text-2xl font-bold text-[hsl(var(--foreground))] mb-2"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    Access Denied
                  </h2>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">
                    The email address{' '}
                    <span className="font-semibold text-[hsl(var(--foreground))]">{email}</span>{' '}
                    is not registered for this event.
                  </p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8">
                    If you believe this is an error, please visit the{' '}
                    <strong>registration desk</strong> for assistance.
                  </p>
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-6 text-left">
                    <div className="flex items-start gap-3">
                      <Shield size={18} className="text-red-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-red-800 mb-1">Not registered?</p>
                        <p className="text-xs text-red-700">
                          Only pre-registered delegates can access the SEDFA Forum Hub. Please contact
                          the registration desk at the venue.
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={resetToEmail}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[hsl(var(--primary))] text-white rounded-lg font-semibold text-sm hover:opacity-90 active:scale-95 transition-all"
                  >
                    ← Try a different email
                  </button>
                </div>
              )}

              {/* STEP: QR CHECK-IN */}
              {step === 'qr-checkin' && delegate && qrToken && (
                <div>
                  <h2
                    className="text-2xl font-bold text-[hsl(var(--foreground))] mb-1"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    Show Your QR Code
                  </h2>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5">
                    Present this QR code to the <strong>registration desk</strong>. Once scanned, you
                    will be prompted to enter your PIN.
                  </p>
                  <div className="rounded-xl border border-[hsl(var(--border))] bg-white p-4 flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {delegate.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-[hsl(var(--foreground))] truncate">{delegate.name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                        {delegate.role} · {delegate.organization}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center bg-white rounded-2xl border border-[hsl(var(--border))] p-6 mb-5">
                    {!qrScanned ? (
                      <>
                        <div className="bg-white p-3 rounded-xl shadow-sm border border-[hsl(var(--border))] mb-4">
                          <QRCodeSVG value={qrValue} size={200} level="H" includeMargin={false} fgColor="hsl(214, 64%, 24%)" />
                        </div>
                        <p className="text-xs font-mono text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] px-3 py-1 rounded-full mb-3">
                          {qrToken.token}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-full justify-center">
                          {polling ? (
                            <><RefreshCw size={12} className="animate-spin" /><span>Waiting for scan at registration desk…</span></>
                          ) : (
                            <><QrCode size={12} /><span>Show this to the registration desk</span></>
                          )}
                        </div>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3 text-center">
                          This QR code is <strong>single-use</strong> and valid for today&apos;s check-in only.
                        </p>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-3 py-4">
                        <CheckCircle2 size={56} className="text-green-500" />
                        <p className="font-semibold text-green-700 text-lg">QR Code Scanned!</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] text-center">
                          Setting up your PIN access…
                        </p>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={resetToEmail}
                    className="w-full text-center text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors"
                  >
                    ← Use a different email
                  </button>
                </div>
              )}

              {/* STEP: ENTER PIN (returning delegate) */}
              {step === 'enter-pin' && delegate && (
                <div>
                  <h2
                    className="text-2xl font-bold text-[hsl(var(--foreground))] mb-1"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    Enter Your PIN
                  </h2>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5">
                    Welcome back, <strong>{delegate.name.split(' ')[0]}</strong>. Enter your 4-digit PIN to access the forum.
                  </p>
                  <div className="rounded-xl border border-[hsl(var(--border))] bg-white p-4 flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {delegate.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-[hsl(var(--foreground))] truncate">{delegate.name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                        {delegate.role} · {delegate.organization}
                      </p>
                    </div>
                  </div>
                  <form onSubmit={handleEnterPin} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-3 text-center">
                        4-Digit PIN
                      </label>
                      <PinInputs digits={pin} setDigits={setPin} refs={pinRefs} />
                    </div>
                    {pinError && (
                      <div className="flex items-center gap-2 text-red-500 text-xs justify-center">
                        <AlertCircle size={13} /><span>{pinError}</span>
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-[hsl(var(--primary))] text-white rounded-lg font-semibold text-sm hover:opacity-90 active:scale-95 transition-all disabled:opacity-60"
                    >
                      {loading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <><LogIn size={16} /><span>Access Forum</span></>
                      )}
                    </button>
                  </form>
                  <div className="mt-5 flex items-center justify-between text-xs">
                    <button
                      onClick={() => { setStep('forgot-email'); setForgotEmail(delegate.email); setForgotEmailError(''); }}
                      className="text-[hsl(var(--primary))] hover:underline font-medium"
                    >
                      Forgot PIN?
                    </button>
                    <button
                      onClick={resetToEmail}
                      className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors"
                    >
                      ← Different email
                    </button>
                  </div>
                </div>
              )}

              {/* STEP: SET PIN (after QR scan + default PIN entered) */}
              {step === 'set-pin' && delegate && (
                <div>
                  <h2
                    className="text-2xl font-bold text-[hsl(var(--foreground))] mb-1"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    Set Your Personal PIN
                  </h2>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
                    Welcome, <strong>{delegate.name.split(' ')[0]}</strong>! Choose a 4-digit PIN you will use to log in going forward.
                  </p>
                  <form onSubmit={handleSetPin} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-3 text-center">
                        Enter PIN
                      </label>
                      <PinInputs digits={pin} setDigits={setPin} refs={pinRefs} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-3 text-center">
                        Confirm PIN
                      </label>
                      <PinInputs digits={confirmPin} setDigits={setConfirmPin} refs={confirmPinRefs} />
                    </div>
                    {pinError && (
                      <div className="flex items-center gap-2 text-red-500 text-xs justify-center">
                        <AlertCircle size={13} /><span>{pinError}</span>
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-[hsl(var(--primary))] text-white rounded-lg font-semibold text-sm hover:opacity-90 active:scale-95 transition-all disabled:opacity-60"
                    >
                      {loading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <><KeyRound size={16} /><span>Set PIN & Access Forum</span></>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}